import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { CheckCircle2, XCircle, Loader2, Truck } from "lucide-react";
import { formatAmount } from "@/lib/currency";
import { Button } from "@/components/ui/button";

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const paypalOrderId = searchParams.get("order_id");
  const isPayPal = searchParams.get("paypal") === "1";
  const isRazorpay = searchParams.get("razorpay") === "1";
  const { clearCart } = useApp();
  const [status, setStatus] = useState("pending");
  const [transaction, setTransaction] = useState(null);
  const attempts = useRef(0);
  const clearedRef = useRef(false);

  useEffect(() => {
    // Razorpay flow — already verified before redirect
    if (isRazorpay && paypalOrderId) {
      // paypalOrderId param is reused as order_id (razorpay order id)
      // Just show success; the verify endpoint was already called in Cart.
      setTransaction({ payment_status: "paid", plan_ids: [], amount: null, currency: "inr" });
      setStatus("paid");
      if (!clearedRef.current) { clearCart(); clearedRef.current = true; }
      return;
    }

    // PayPal flow — already captured before redirect
    if (isPayPal && paypalOrderId) {
      (async () => {
        try {
          const { data } = await api.post(`/paypal/capture-order/${paypalOrderId}`);
          setTransaction(data);
          setStatus(data.payment_status === "paid" ? "paid" : "error");
          if (data.payment_status === "paid" && !clearedRef.current) {
            clearCart();
            clearedRef.current = true;
          }
        } catch (err) {
          // [removed console.debug]
          setStatus("error");
        }
      })();
      return;
    }

    // Stripe flow — polling
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setTransaction(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!clearedRef.current) {
            clearCart();
            clearedRef.current = true;
          }
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts.current >= 8) {
          setStatus("timeout");
          return;
        }
        attempts.current += 1;
        setTimeout(poll, 2000);
      } catch (err) {
        // [removed console.debug]
        if (attempts.current >= 8) {
          setStatus("error");
          return;
        }
        attempts.current += 1;
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId, paypalOrderId, isPayPal, isRazorpay, clearCart]);

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-6 py-24 lg:py-32 text-center">
        {status === "pending" && (
          <>
            <Loader2 size={40} className="mx-auto text-accent animate-spin" />
            <div className="overline text-accent mt-6">Confirming</div>
            <h1 className="font-display text-5xl mt-3">Processing payment…</h1>
            <p className="mt-4 text-muted-foreground">This usually takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={40} className="mx-auto text-accent" />
            <div className="overline text-accent mt-6">Payment successful</div>
            <h1 className="font-display text-5xl mt-3">Thank you.</h1>
            <p className="mt-6 text-lg text-muted-foreground">
              Your order has been received successfully. A receipt has been sent to your email.
            </p>
            {transaction && (
              <div className="mt-8 inline-block border border-border p-6 text-left" data-testid="tx-details">
                <div className="overline text-muted-foreground">Order total</div>
                <div className="font-display text-3xl mt-1 text-primary">
                  {formatAmount(transaction.amount, transaction.currency)}
                </div>
                <div className="text-xs text-muted-foreground mt-3">{transaction.plan_ids?.length} plan(s)</div>
              </div>
            )}
            {/* Iter48 — Manual delivery message for CAD/Revit orders */}
            {transaction?.manual_delivery_required && (
              <div className="mt-8 max-w-lg mx-auto border border-accent/40 bg-accent/5 p-5 text-left flex gap-3" data-testid="manual-delivery-message">
                <Truck size={18} className="text-accent shrink-0 mt-0.5" />
                <div>
                  <div className="overline text-accent">Manual delivery required</div>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    Our team will review your order and deliver your CAD/Revit files to your registered
                    email address within our committed delivery timeframe. Any PDF files in your order
                    are already available under &quot;My plans&quot;.
                  </p>
                </div>
              </div>
            )}
            <div className="mt-10 flex gap-4 justify-center">
              <Link to="/purchases">
                <Button className="rounded-none bg-primary h-12 px-8" data-testid="view-purchases-btn">View my plans</Button>
              </Link>
              <Link to="/plans">
                <Button variant="outline" className="rounded-none h-12 px-8 border-foreground">Continue browsing</Button>
              </Link>
            </div>
          </>
        )}
        {(status === "expired" || status === "error" || status === "timeout") && (
          <>
            <XCircle size={40} className="mx-auto text-destructive" />
            <div className="overline text-destructive mt-6">Something went wrong</div>
            <h1 className="font-display text-5xl mt-3">Payment not confirmed.</h1>
            <p className="mt-4 text-muted-foreground">
              {status === "expired" && "The session expired. Please try again."}
              {status === "timeout" && "We couldn't confirm payment in time. Check your email or try again."}
              {status === "error" && "An error occurred confirming payment."}
            </p>
            <Link to="/cart">
              <Button className="rounded-none bg-primary h-12 px-8 mt-8">Back to cart</Button>
            </Link>
          </>
        )}
      </div>
    </Layout>
  );
};

export default CheckoutSuccess;
